import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { X, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  getFeedbackFormByTitle,
  submitFeedbackResponse,
} from "../../api/feedbackService";
import { useAuth } from "../../hooks/useAuth";
import { FeedbackResponse, FeedbackForm } from "./types";

import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/Dialog";
import { RadioGroup, RadioGroupItem } from "../../components/ui/RadioGroup";
import { Label } from "../../components/ui/Label";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/Alert";

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  isOpen,
  onClose,
  title,
}) => {
  const { user } = useAuth();
  const [responses, setResponses] = useState<Record<string, string | string[]>>(
    {}
  );
  const [status, setStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Array<{ questionId: string; message: string }>
  >([]);

  const { data: form, isLoading: formLoading } = useQuery({
    queryKey: ["feedbackForm", title],
    queryFn: () => getFeedbackFormByTitle(title),
    enabled: isOpen,
  });

  const submitMutation = useMutation({
    mutationFn: (responseData: { responses: FeedbackResponse[] }) =>
      submitFeedbackResponse(form?.id || "", user?.uid || "", {
        ...responseData,
        submittedAt: new Date(),
      }),
    onSuccess: () => {
      setStatus({
        type: "success",
        message: "Thank you for your feedback!",
      });
      setTimeout(() => {
        onClose();
        setResponses({});
        setStatus(null);
        setValidationErrors([]);
      }, 1500);
    },
    onError: (error: Error) => {
      setStatus({
        type: "error",
        message: error.message,
      });
    },
  });

  const handleInputChange = (
    questionId: string,
    value: string | string[],
    isMultiple = false
  ) => {
    setResponses((prev) => {
      const newValue = isMultiple
        ? Array.isArray(prev[questionId])
          ? (prev[questionId] as string[]).includes(value as string)
            ? (prev[questionId] as string[]).filter((v) => v !== value)
            : [...(prev[questionId] as string[]), value as string]
          : [value as string]
        : value;
      return {
        ...prev,
        [questionId]: newValue,
      };
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!user?.uid) {
      setStatus({
        type: "error",
        message: "You must be logged in to submit feedback",
      });
      return;
    }

    if (!form) {
      setStatus({
        type: "error",
        message: "Form not found",
      });
      return;
    }

    // Validate required fields
    const errors = form.questions
      .filter((q) => {
        if (!q.isRequired) return false;
        const response = responses[q.id];
        if (q.type === "multiple_choice" && q.selectionType === "multiple") {
          return (
            !response || (Array.isArray(response) && response.length === 0)
          );
        }
        return !response;
      })
      .map((q) => ({
        questionId: q.id,
        message: `"${q.question}" is required`,
      }));

    if (errors.length > 0) {
      setValidationErrors(errors);
      setStatus({
        type: "error",
        message: "Please fill in all required fields",
      });
      return;
    }

    setValidationErrors([]);
    submitMutation.mutate({
      responses: Object.entries(responses).map(([questionId, value]) => ({
        questionId,
        value,
      })),
    });
  };

  const renderQuestion = (question: FeedbackForm["questions"][0]) => {
    switch (question.type) {
      case "text":
        return (
          <Input
            id={`question-${question.id}`}
            value={responses[question.id] || ""}
            placeholder="Your answer"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleInputChange(question.id, e.target.value)
            }
            required={question.isRequired}
            className="w-full border-gray-200 focus:border-primary focus:ring-primary"
          />
        );
      case "multiple_choice":
        if (question.selectionType === "multiple") {
          return (
            <div className="space-y-3">
              {question.options?.map((option) => (
                <div key={option.id} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id={`option-${option.id}`}
                    checked={(
                      (responses[question.id] as string[]) || []
                    ).includes(option.text)}
                    onChange={() =>
                      handleInputChange(question.id, option.text, true)
                    }
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 focus:ring-2 checked:bg-primary checked:border-transparent hover:border-primary transition-colors"
                  />
                  <Label
                    htmlFor={`option-${option.id}`}
                    className="text-gray-700 text-sm cursor-pointer select-none"
                  >
                    {option.text}
                  </Label>
                </div>
              ))}
            </div>
          );
        }
        return (
          <RadioGroup
            value={(responses[question.id] as string) || ""}
            onValueChange={(value: string) =>
              handleInputChange(question.id, value)
            }
            className="space-y-3"
          >
            {question.options?.map((option) => (
              <div key={option.id} className="flex items-center space-x-3">
                <RadioGroupItem
                  value={option.text}
                  id={`option-${option.id}`}
                  className="border-gray-300 text-primary focus:ring-primary"
                />
                <Label
                  htmlFor={`option-${option.id}`}
                  className="text-gray-700 text-sm"
                >
                  {option.text}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );
      case "rating":
        return (
          <div className="flex space-x-2">
            {[1, 2, 3, 4, 5].map((num) => (
              <Button
                key={num}
                type="button"
                variant={
                  responses[question.id] === num.toString()
                    ? "default"
                    : "outline"
                }
                onClick={() => handleInputChange(question.id, num.toString())}
                className={`w-10 h-10 ${
                  responses[question.id] === num.toString()
                    ? "bg-[var(--color-primary)] text-white hover:bg-[var(--primary-700)]"
                    : "hover:border-[var(--primary-500)] hover:text-[var(--primary-600)]"
                }`}
              >
                {num}
              </Button>
            ))}
          </div>
        );
      case "scale":
        return (
          <div className="flex flex-wrap gap-2">
            {[...Array(10)].map((_, i) => (
              <Button
                key={i}
                type="button"
                variant={
                  responses[question.id] === (i + 1).toString()
                    ? "default"
                    : "outline"
                }
                onClick={() =>
                  handleInputChange(question.id, (i + 1).toString())
                }
                className={`w-10 h-10 ${
                  responses[question.id] === (i + 1).toString()
                    ? "bg-primary text-white hover:bg-primary"
                    : "hover:border-primary hover:text-primary"
                }`}
              >
                {i + 1}
              </Button>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-6 bg-white">
        <DialogHeader className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl font-semibold text-gray-900">
                {form?.title || title}
              </DialogTitle>
              {form?.description && (
                <DialogDescription className="mt-2 text-gray-600">
                  {form.description}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        {formLoading ? (
          <div className="py-8 text-center text-gray-600">Loading...</div>
        ) : !form ? (
          <div className="py-8 text-center text-red-500">Form not found</div>
        ) : (
          <>
            {status && (
              <Alert
                variant={status.type === "error" ? "destructive" : "default"}
                className="mb-6"
              >
                {status.type === "error" ? (
                  <AlertCircle className="h-4 w-4" />
                ) : (
                  <CheckCircle2 className="h-4 bg-primary w-4" />
                )}
                <AlertTitle className="font-semibold">
                  {status.type === "error" ? "Error" : "Success"}
                </AlertTitle>
                <AlertDescription>{status.message}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-8">
              {form.questions.map((question) => (
                <div
                  key={question.id}
                  className="space-y-4 pb-4 border-b border-gray-100 last:border-0"
                >
                  <Label
                    htmlFor={`question-${question.id}`}
                    className="block text-base font-medium text-gray-900"
                  >
                    {question.question}
                    {question.isRequired && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </Label>
                  <div className="mt-2">{renderQuestion(question)}</div>
                  {validationErrors.find(
                    (e) => e.questionId === question.id
                  ) && (
                    <p className="text-sm text-red-500 mt-1">
                      {
                        validationErrors.find(
                          (e) => e.questionId === question.id
                        )?.message
                      }
                    </p>
                  )}
                </div>
              ))}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitMutation.isLoading}
                  className="bg-primary text-white hover:bg-purple"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit Feedback
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
